import React, { memo, useCallback } from "react";
import moment from "moment";
import { FiLock } from "react-icons/fi";

/**
 * Click-to-edit date cell. Nothing saves on pick — the value goes into the
 * editor's draft state and is written when the whole schedule is saved.
 *
 * Ported from the portal's react-datepicker cell to a native date input
 * (reno-web has no react-datepicker); the border semantics are identical:
 * amber = conflict flagged by the preview, red = empty, grey = normal.
 *
 * Memoised because a step can hold dozens of these — without it, changing
 * one date re-rendered every picker on the screen.
 */
const DateCell = ({
  value,
  onChange,
  locked = false,
  lockReason = null,
  originalValue = undefined,
  placeholder = "Select date",
  conflict = false,
  hideLockIcon = false,
}) => {
  const display = value ? moment(value).format("DD MMM, YYYY") : placeholder;
  const isDirty =
    originalValue !== undefined && (originalValue || null) !== (value || null);

  const handleChange = useCallback(
    (e) => onChange(e.target.value ? e.target.value : null),
    [onChange],
  );

  if (locked) {
    return (
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        title={lockReason ? `Locked: ${lockReason}` : "Locked"}
      >
        {/* A locked cell can still be about to move — a derived date follows
            the activity dates being edited a step earlier. Show where it
            lands, with the current value struck through beside it. */}
        <span
          style={
            isDirty
              ? { color: "#111827", fontWeight: 500 }
              : { color: "#6B7280" }
          }
        >
          {display}
        </span>
        {isDirty && originalValue && (
          <span
            style={{
              fontSize: "12px",
              color: "#9CA3AF",
              textDecoration: "line-through",
            }}
          >
            {moment(originalValue).format("DD MMM")}
          </span>
        )}
        {!hideLockIcon && <FiLock size={12} style={{ color: "#9CA3AF" }} />}
      </span>
    );
  }

  // A single border carries the state — amber is reserved for a date that is
  // actually WRONG; an edited date stays neutral (the struck-through original
  // beside it and the "Unsaved changes" chip already say what changed).
  const borderColor = conflict ? "#F59E0B" : !value ? "#F87171" : "#D1D5DB";

  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <input
        type="date"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: "4px",
          padding: "4px 8px",
          fontSize: "14px",
          width: "144px",
          color: conflict ? "#78350F" : "#0d0d0e",
          backgroundColor: conflict ? "#FFFBEB" : "#fff",
          outline: "none",
          fontFamily: "inherit",
          boxSizing: "border-box",
        }}
      />
      {/* The struck-through "before" value only means something when there
          was a value to begin with — otherwise it rendered as a bare dash. */}
      {isDirty && originalValue && (
        <span
          style={{
            fontSize: "12px",
            color: "#9CA3AF",
            textDecoration: "line-through",
          }}
          title="Original value"
        >
          {moment(originalValue).format("DD MMM")}
        </span>
      )}
    </div>
  );
};

export default memo(DateCell);
